import ControllerBase from '../../../../global/js/ControllerBase';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { AtomQuantitySelectorEvents } from './a-quantity-selector.events';
import { CartItemUpdate } from '../../organisms/o-shopping-cart/o-shopping-cart.model';

export default class ControllerQuantitySelector extends ControllerBase {
	/** A list of dom element references available to this component */
	private readonly componentRefs = {
		addQuantity: 'addQuantity',
		subtractQuantity: 'subtractQuantity',
		quantity: 'quantity',
		quantityLabel: 'quantityLabel',
		productId: 'productId',
	};

	private $quantity: HTMLInputElement;

	/** The html data attribs on the component */
	private readonly dataAttributes = {
		minQuantity: 'minQuantity',
		maxQuantity: 'maxQuantity',
	};

	/** The minimum quantity a user can choose */
	private minQuantity: number;
	/** The minimum quantity default if none provided */
	private readonly minQuantityDefault = 0;
	/** The maximum quantity a user can choose */
	protected maxQuantity: number;
	/** The maximum quantity default if none provided */
	private readonly maxQuantityDefault = 9;

	private productId: string;
	private onQuantityChanged: BehaviorSubject<number> = new BehaviorSubject(-1);
	// private currentQuantity: number;

	/** The API debounce timeout */
	private apiDebounceTimeout: any = null;
	/** The API debounce timeout duration */
	private readonly apiDebounceWait = 750; // in ms

	constructor(element: HTMLElement) {
		super(element);

		this.$quantity = <HTMLInputElement>this.$references[this.componentRefs.quantity];
		this.productId = (<HTMLInputElement>this.$references[this.componentRefs.productId]).value;

		// subscribe to internal quantity changes and update the DOM
		this.onQuantityChanged.subscribe((newQuantity: number) => {
			if (newQuantity >= 0) {
				this.updateQuantityHtml(newQuantity);
			}
		});

		// init current quantity
		// this.currentQuantity = this.getCurrentQuantity();
		this.onQuantityChanged.next(this.getCurrentQuantity());

		// determine the min/max quantity values
		this.setMinMaxQuantities();

		this.registerEventListeners();
	}

	/**
	 * Registers all events
	 * @private
	 */
	private registerEventListeners(): void {
		// watch for adding
		this.add();

		// watch for subtracting
		this.substract();
	}

	/**
	 * Initialize the min/max quantity values based on the component data attribs
	 */
	private setMinMaxQuantities(): void {
		this.minQuantity = this.minQuantityDefault;
		this.maxQuantity = this.maxQuantityDefault;

		if (this.$element && this.$element.dataset) {
			if (Object.prototype.hasOwnProperty.call(this.$element.dataset, this.dataAttributes.minQuantity)) {
				this.minQuantity = Number(this.$element.dataset[this.dataAttributes.minQuantity]);
			}

			if (Object.prototype.hasOwnProperty.call(this.$element.dataset, this.dataAttributes.maxQuantity)) {
				this.maxQuantity = Number(this.$element.dataset[this.dataAttributes.maxQuantity]);
			}
		}
	}

	/**
	 * Observe adding product items
	 */
	private add(): void {
		if (Object.prototype.hasOwnProperty.call(this.$references, this.componentRefs.addQuantity)) {
			/** Event handler for when the quantity is increased */
			const addQuantity$ = fromEvent(this.$references[this.componentRefs.addQuantity], 'click');

			addQuantity$.subscribe((e: Event | MouseEvent) => {
				e.preventDefault();
				if (e && e.target) {
					const currentQuantity = this.onQuantityChanged.getValue();
					const newQuantity = currentQuantity + 1;

					if (currentQuantity.toString().length && this.maxQuantity && currentQuantity < this.maxQuantity) {
						// this.updateQuantity(currentQuantity + 1);

						// store new qty for later
						this.onQuantityChanged.next(newQuantity);

						// reset debounce, if set
						if (this.apiDebounceTimeout) {
							clearTimeout(this.apiDebounceTimeout);
							this.apiDebounceTimeout = null;
							this.apiDebounce();
						} else {
							this.apiDebounce();
						}
					}
				}
			});
		}
	}

	/**
	 * Observe subtracting product items
	 */
	private substract(): void {
		if (Object.prototype.hasOwnProperty.call(this.$references, this.componentRefs.subtractQuantity)) {
			/** Event handler for when the button is clicked */
			const subtractQuantity$ = fromEvent(this.$references[this.componentRefs.subtractQuantity], 'click');

			subtractQuantity$.subscribe((e: Event | MouseEvent) => {
				e.preventDefault();
				if (e && e.target) {
					const currentQuantity = this.onQuantityChanged.getValue();
					const newQuantity = currentQuantity - 1;

					if (newQuantity >= 0 && newQuantity >= this.minQuantity) {
						// store new qty for later
						this.onQuantityChanged.next(newQuantity);

						// reset debounce, if set
						if (this.apiDebounceTimeout) {
							clearTimeout(this.apiDebounceTimeout);
							this.apiDebounceTimeout = null;
							this.apiDebounce();
						} else {
							this.apiDebounce();
						}
					}
				}
			});
		}
	}

	/**
	 * Debounce requests to the API and get the latest quantity value the user has chosen
	 * @returns void
	 */
	private apiDebounce(): void {
		this.apiDebounceTimeout = setTimeout(() => {
			this.updateQuantity(this.onQuantityChanged.getValue());
			// @todo - clear timeout?
		}, this.apiDebounceWait);
	}

	/**
	 * Update the quantity value in the DOM
	 * @param newQuantity The new quantity value to update the DOM with
	 */
	private updateQuantityHtml(newQuantity: number): void {
		this.domUtils.renderDomElement(this.componentRefs.quantityLabel, newQuantity.toString());
		this.$quantity.value = <string>(<unknown>newQuantity);
	}

	/**
	 * Emit the new quantity value to listeners
	 * @param newQuantity The new quantity value
	 */
	private updateQuantity(quantity: number): void {
		// build event data
		const eventDetail: CartItemUpdate = {
			productId: this.productId,
			quantity: quantity,
		};
		this.dispatchCustomEvent(AtomQuantitySelectorEvents.quantityChangeEvent, eventDetail, true);
	}

	/**
	 * Gets the current quantity from the DOM
	 */
	private getCurrentQuantity(): number {
		return +(<HTMLInputElement>this.$references[this.componentRefs.quantity]).value;
	}
}
